import React from "react";

import get from "lodash/get";
import isEqual from "lodash/isEqual";
import isInteger from "lodash/isInteger";

import {
  dateFormatter,
  isValidDate,
  isBefore,
  isSameOrBefore,
  isSameOrAfter,
  isAfter,
  DEFAULT_DATE_FORMAT,
} from "@stadium/helpers/dayjs";

import {
  ALPHA_REGEX,
  EMAIL_REGEX,
  MAXIMUM_IMAGE_SIZE_MB,
  MAXIMUM_IMAGE_SIZE,
  acceptedImageFormats,
  acceptedImageFormatsRegex,
  PASSWORD_REGEX,
  URL_REGEX,
} from "../form/constants";

export type ValidationResultType = {
  valid: boolean;
  error?: string;
};

export type validatesType = {
  presence?: boolean;
  // eslint-disable-next-line no-unused-vars
  runValidations?: (formValues: FormValuesType) => boolean;
  compare?: {
    lesserThan?: number;
    greaterThan?: number;
    lesserThanOrEqualTo?: number;
    greaterThanOrEqualTo?: number;
    // eslint-disable-next-line no-unused-vars
    getError?: (args: {
      key: "lesserThan" | "greaterThan" | "lesserThanOrEqualTo" | "greaterThanOrEqualTo";
      restriction: number;
      value: number;
    }) => React.ReactNode;
  };
  currency?: boolean;
  email?: boolean;
  equals?: { compareTo: string };
  length?: { max?: number; min?: number };
  integerOnly?: boolean;
  compareDate?: {
    before?: Date;
    after?: Date;
    sameOrBefore?: Date;
    sameOrAfter?: Date;
    format?: string;
  };
};

export type FormFieldType = {
  name: string;
  value: string | number | File | boolean;
  validates: validatesType;
  type?:
    | "text"
    | "textarea"
    | "radio"
    | "checkbox"
    | "select"
    | "select-with-search"
    | "rich-text-area";
  variant?: "number" | "password" | "email" | "text";
  label?: React.ReactNode;
  placeholder?: string;
};

export type FormValuesType<T = FormFieldType> = {
  [name: string]: T & { selectedValueLabel?: React.ReactNode };
};

export type FormErrorsType<T = FormValuesType> = {
  // eslint-disable-next-line no-unused-vars
  [k in keyof T]?: string[];
};

export type ValidatorArgs = {
  validates: validatesType;
  value: FormFieldType["value"];
  formValues?: FormValuesType;
};

export type StrictStringValidatorArgs = ValidatorArgs & { value: string };

export const STATUS_VALID: ValidationResultType = { valid: true, error: "" };
export const STATUS_INVALID: ValidationResultType = { valid: false };

export const validatePassword = (
  password: string = "",
  invalidPasswordMessage: string = "",
  acceptedPasswordRegex: RegExp = PASSWORD_REGEX,
) => {
  if (password?.trim().length > 0) {
    if (acceptedPasswordRegex.test(password)) {
      return STATUS_VALID;
    }
    return { ...STATUS_INVALID, error: invalidPasswordMessage };
  }

  return { ...STATUS_INVALID, error: "Required" };
};
export const isEmail = (value: string) => {
  const res = EMAIL_REGEX.exec(value.toString());
  return res !== null;
};
export const isUrl = (value: string) => {
  const res = URL_REGEX.exec(value.toString());
  return res !== null;
};
export const isNumber = (value: string | number) => {
  const res = /\D/.exec(value.toString());
  return res === null;
};
export const isEmpty = (value: any) => value.toString().trim() === "";
export const isLength = (value: string | number, len: number) =>
  !!(value && value.toString().length === len);
export const maxStrLength = (val: string, length: number) => {
  return isInteger(length) && typeof val === "string" && val.length > length;
};
export const minStrLength = (val: string, length: number) => {
  return isInteger(length) && typeof val === "string" && val.length < length;
};

const presenceValidator = ({ validates = {}, value = "" }: StrictStringValidatorArgs) => {
  const { presence } = validates;
  if (presence && isEmpty(value)) {
    return { ...STATUS_INVALID, error: "Required" };
  }
  return STATUS_VALID;
};

const lengthValidator = ({ validates = {}, value = "" }: StrictStringValidatorArgs) => {
  const lengthObj = get(validates, "length");
  if (maxStrLength(value, lengthObj.max)) {
    return { ...STATUS_INVALID, error: `Max ${lengthObj.max} characters` };
  }
  if (minStrLength(value, lengthObj.min)) {
    return { ...STATUS_INVALID, error: `Min ${lengthObj.min} characters` };
  }
  return STATUS_VALID;
};

const emailValidator = ({ value = "" }: StrictStringValidatorArgs) => {
  if (!isEmail(value)) {
    return { ...STATUS_INVALID, error: "Invalid Email" };
  }
  return STATUS_VALID;
};

const passwordValidator = ({ value = "" }: StrictStringValidatorArgs) => {
  const { valid, error: passwordError } = validatePassword(value);
  if (!valid) {
    return { ...STATUS_INVALID, error: passwordError };
  }
  return STATUS_VALID;
};

const equalityValidator = ({ validates = {}, value = "", formValues = {} }: ValidatorArgs) => {
  const formFieldToCompare = validates.equals?.compareTo;
  if (!isEqual(value, formValues[formFieldToCompare]?.value)) {
    return { ...STATUS_INVALID, error: `Must be same as new ${formFieldToCompare}` };
  }
  return STATUS_VALID;
};

const compareValidator = ({
  validates = {},
  value = "",
  formValues,
}: StrictStringValidatorArgs) => {
  const {
    compare: { lesserThan, greaterThan, lesserThanOrEqualTo, greaterThanOrEqualTo, getError },
  } = validates;

  const presence = presenceValidator({ validates, value, formValues }).valid;
  if (presence && !value) {
    return STATUS_VALID;
  }

  const quantityComparison: {
    property: string;
    num: number;
    // eslint-disable-next-line no-unused-vars
    op: (a: number, b: number) => boolean;
    key: "lesserThan" | "greaterThan" | "lesserThanOrEqualTo" | "greaterThanOrEqualTo";
  }[] = [
    {
      property: "lesser than",
      num: lesserThan,
      op: (a: number, b: number) => a < b,
      key: "lesserThan",
    },
    {
      property: "greater than",
      num: greaterThan,
      op: (a: number, b: number) => a > b,
      key: "greaterThan",
    },
    {
      property: "lesser than or equal to",
      num: lesserThanOrEqualTo,
      op: (a: number, b: number) => a <= b,
      key: "lesserThanOrEqualTo",
    },
    {
      property: "greater than or equal to",
      num: greaterThanOrEqualTo,
      op: (a: number, b: number) => a >= b,
      key: "greaterThanOrEqualTo",
    },
  ];

  for (const comparison of quantityComparison) {
    const { property, num, op } = comparison;
    const propertyNumber = Number(num);
    const valueNum = Number(value);
    if (!Number.isNaN(propertyNumber) && !op(valueNum, propertyNumber)) {
      return {
        ...STATUS_INVALID,
        error:
          getError?.({ key: comparison.key, restriction: propertyNumber, value: valueNum }) ||
          `Must be ${property} ${propertyNumber}`,
      };
    }
  }
  return STATUS_VALID;
};

const phoneNumberValidator = ({
  validates = {},
  value = "",
  formValues,
}: StrictStringValidatorArgs) => {
  const isPresenceValid = presenceValidator({ validates, value, formValues }).valid;

  if (value && !ALPHA_REGEX.test(value)) {
    return STATUS_VALID;
  }

  if (isPresenceValid && value?.trim() === "") {
    return STATUS_VALID;
  }

  return { ...STATUS_INVALID, error: "Invalid Contact Number" };
};

export const compareRegexValidator = ({ validates = {}, value = "" }) => {
  const {
    compareRegex: { regex, error },
  } = validates;

  if (regex.test(value)) {
    return STATUS_VALID;
  }
  return { ...STATUS_INVALID, error: error || "Invalid Pattern" };
};

const integerOnlyValidator = ({ value = "" }: StrictStringValidatorArgs) => {
  const numberValue = Number(value);
  if (!isNaN(numberValue) && Math.floor(numberValue) === numberValue) {
    return { ...STATUS_VALID };
  }
  return {
    ...STATUS_INVALID,
    error: "Invalid integer",
  };
};

const currencyValidator = ({ value = "" }: StrictStringValidatorArgs) => {
  const numberValue = Number(value);
  if (!isNaN(numberValue) && Number(numberValue.toFixed(2)) === numberValue) {
    return { ...STATUS_VALID };
  }
  return {
    ...STATUS_INVALID,
    error: "Invalid currency",
  };
};

const compareDateValidator = ({ validates = {}, value = "" }: StrictStringValidatorArgs) => {
  const {
    compareDate: { before, after, sameOrBefore, sameOrAfter, format = DEFAULT_DATE_FORMAT },
  } = validates;

  const dateComparison = [
    {
      property: "before",
      date: before,
      op: (primaryDate: Date, secondaryDate: Date) => isBefore(primaryDate, secondaryDate, "date"),
    },
    {
      property: "after",
      date: after,
      op: (primaryDate: Date, secondaryDate: Date) => isAfter(primaryDate, secondaryDate, "date"),
    },
    {
      property: "same or before",
      date: sameOrBefore,
      op: (primaryDate: Date, secondaryDate: Date) =>
        isSameOrBefore(primaryDate, secondaryDate, "date"),
    },
    {
      property: "same or after",
      date: sameOrAfter,
      op: (primaryDate: Date, secondaryDate: Date) =>
        isSameOrAfter(primaryDate, secondaryDate, "date"),
    },
  ];

  for (const comparison of dateComparison) {
    const { property, date, op } = comparison;
    const valueDate = dateFormatter(value).toDate();
    if (date && isValidDate(date) && !op(valueDate, date)) {
      return { ...STATUS_INVALID, error: `Must be ${property} ${dateFormatter(date, { format })}` };
    }
  }
  return STATUS_VALID;
};

export const validateFileSize = (inputFile, maxSize) => inputFile?.size <= maxSize;

export const validateFileType = (inputFile, acceptedFileTypeRegex) =>
  Boolean(inputFile?.type?.match(acceptedFileTypeRegex));

export const getImageDimension = (inputPhoto) => {
  const imageUrl = URL.createObjectURL(inputPhoto);
  const image = new Image();
  const promise = new Promise((resolve, reject) => {
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      URL.revokeObjectURL(imageUrl);
      resolve({ width, height });
    };
    image.onerror = reject;
  });
  image.src = imageUrl;
  return promise;
};

export const validateImageDimension = async ({
  inputPhoto,
  dimension: { height = 0, width = 0 },
}) => {
  try {
    const { width: imageWidth, height: imageHeight } = await getImageDimension(inputPhoto);
    if (width && height) {
      return width === imageWidth && height === imageHeight;
    }

    if (width) {
      return width === imageWidth;
    }

    return height === imageHeight;
  } catch (err) {
    return {};
  }
};

export const asyncImageValidator = async (inputPhoto, validations = {}) => {
  const {
    size: {
      maxInMb: maxSizeMb = MAXIMUM_IMAGE_SIZE_MB,
      maxInBytes: maxSize = MAXIMUM_IMAGE_SIZE,
    } = {},
    type: {
      acceptedFormats = acceptedImageFormats,
      acceptedFormatsRegex = acceptedImageFormatsRegex,
    } = {},
    dimension = null,
    presence,
  } = validations;

  const validators = [];

  const fileSizeValidator = () => {
    const isFileSizeValid = validateFileSize(inputPhoto, maxSize);
    return {
      valid: isFileSizeValid,
      errors: isFileSizeValid ? "" : `Image size cannot exceed ${maxSizeMb}MB`,
    };
  };

  const fileTypeValidator = () => {
    const isFileTypeValid = validateFileType(inputPhoto, acceptedFormatsRegex);
    return {
      valid: isFileTypeValid,
      errors: isFileTypeValid ? "" : `Accepted image format(s) is ${acceptedFormats.join(", ")}`,
    };
  };

  const fileDimensionValidator = async () => {
    const isFileDimensionValid = await validateImageDimension({ inputPhoto, dimension });
    return {
      valid: isFileDimensionValid,
      errors: isFileDimensionValid
        ? ""
        : `Image dimensions must be ${dimension.width} px X ${dimension.height} px`,
    };
  };

  if (inputPhoto) {
    if (maxSize) {
      validators.push(fileSizeValidator);
    }

    if (maxSize) {
      validators.push(fileTypeValidator);
    }
    if (dimension) {
      validators.push(fileDimensionValidator);
    }

    for (const validator of validators) {
      const { valid, errors } = await validator();
      if (!valid) {
        return { valid, errors };
      }
    }
    return { valid: true };
  }

  return presence ? { valid: true } : { valid: false, error: "Required" };
};

export const VALIDATORS = {
  presence: presenceValidator,
  length: lengthValidator,
  email: emailValidator,
  password: passwordValidator,
  equals: equalityValidator,
  compare: compareValidator,
  phoneNumber: phoneNumberValidator,
  integerOnly: integerOnlyValidator,
  currency: currencyValidator,
  asyncImageValidator: asyncImageValidator,
  compareDate: compareDateValidator,
  compareRegex: compareRegexValidator,
};
